import React, { useState } from 'react';

interface GoogleAuthSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GoogleAuthSetupModal: React.FC<GoogleAuthSetupModalProps> = ({ isOpen, onClose }) => {
  const [showSteps, setShowSteps] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Google OAuth Setup Required</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-sm text-amber-800">
                    Google Sign-In is not configured. To enable Google authentication, you need to set up a Google OAuth application.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Currently using demo configuration. For production use, please configure Google OAuth credentials.
            </p>

            <button
              onClick={() => setShowSteps(!showSteps)}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              {showSteps ? 'Hide' : 'Show'} Setup Instructions
              <svg 
                className={`w-4 h-4 ml-1 transition-transform ${showSteps ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showSteps && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Setup Instructions:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a></li>
                  <li>Create a new project or select an existing one</li>
                  <li>Enable the Google+ API</li>
                  <li>Go to "Credentials" in the left sidebar</li>
                  <li>Click "Create Credentials" → "OAuth 2.0 Client IDs"</li>
                  <li>Set Application type to "Web application"</li>
                  <li>Add these URLs to "Authorized JavaScript origins":
                    <div className="bg-white rounded border p-2 mt-1 font-mono text-xs">
                      http://localhost:3003<br/>
                      https://yourdomain.com
                    </div>
                  </li>
                  <li>Add these URLs to "Authorized redirect URIs":
                    <div className="bg-white rounded border p-2 mt-1 font-mono text-xs">
                      http://localhost:3003/auth/google/callback<br/>
                      https://yourdomain.com/auth/google/callback
                    </div>
                  </li>
                  <li>Copy the Client ID and update your <code className="bg-white px-1 rounded">.env</code> file:
                    <div className="bg-white rounded border p-2 mt-1 font-mono text-xs">
                      REACT_APP_GOOGLE_CLIENT_ID=your-actual-client-id.apps.googleusercontent.com
                    </div>
                  </li>
                  <li>Restart your development server</li>
                </ol>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => window.open('https://console.cloud.google.com/', '_blank')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Open Google Console
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAuthSetupModal;